import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react'
import { resolveImageDisplayWidth } from './imageResize'
import css from './components/TextEditorImage/TextEditorImage.module.css'

interface ImageElement {
    width?: number
    imgWidth?: number
    url?: string
}

// Shared image load/width machinery for both renderers of an img node — the editor
// (TextEditorImage) and the read view (StaticImage) — so the two stay in lockstep per the
// CLAUDE.md editor↔read-view parity rule instead of by copy-paste (SCD-949). Handles:
// - applying the display width (stored `element.width` or natural fallback),
// - a no-fade "instant" state when the image is already cached on mount (no placeholder flash),
// - re-applying the width after a commit / external `element.width` change,
// - resetting when the node is reused with a different `url`.
export function useImageDisplay(imgRef: RefObject<HTMLImageElement | null>, element: ImageElement) {
    const [imageReady, setImageReady] = useState(false)
    const [instantReady, setInstantReady] = useState(false)
    const readyRef = useRef(false)
    const dpr = window.devicePixelRatio || 1

    const applyWidth = () => {
        if (!imgRef.current) return
        const stored = resolveImageDisplayWidth(element, dpr)
        // Fall back to the DOM natural size when the node carries no width/imgWidth (e.g. pasted images).
        const width = stored ?? Math.round(imgRef.current.naturalWidth / dpr)
        imgRef.current.style.width = `${width}px`
    }

    const markLoaded = (cached: boolean) => {
        if (readyRef.current) return
        readyRef.current = true
        applyWidth()
        if (cached) setInstantReady(true)
        setImageReady(true)
    }

    const handleLoad = () => markLoaded(false)

    // Reset when the src changes (a reused component instance pointed at a different image),
    // then re-detect a cached image below — otherwise the new image keeps the old width / stays hidden.
    useLayoutEffect(() => {
        readyRef.current = false
        setImageReady(false)
        setInstantReady(false)
        // A cached image can already be `complete` before onLoad fires. Detect it before paint so
        // it renders sized and visible with no placeholder/fade flash.
        if (imgRef.current?.complete && imgRef.current.naturalWidth > 0) markLoaded(true)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [element.url])

    // Re-apply the stored width after a resize commit / external change.
    useEffect(() => {
        if (imageReady) applyWidth()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [element.width, imageReady])

    const imageStateClass = !imageReady ? css.image_hidden : instantReady ? '' : css.image_visible

    return { dpr, imageReady, imageStateClass, handleLoad, applyWidth }
}
